import React, {useState, useEffect, useRef} from "react";
import CheckJobSiteSection from "./CheckJobSiteSection";

// Optimized scroll animation hook
const useScrollAnimation = (threshold = 0.15, delay = 0) => {
	const [isVisible, setIsVisible] = useState(false);
	const ref = useRef();

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isVisible) {
					if (delay > 0) {
						setTimeout(() => setIsVisible(true), delay);
					} else {
						setIsVisible(true);
					}
					observer.unobserve(entry.target); // Stop observing once animated
				}
			},
			{
				threshold,
				rootMargin: "0px 0px -50px 0px", // Trigger slightly before element is fully visible
			},
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => observer.disconnect();
	}, [threshold, delay, isVisible]);

	return {ref, isVisible};
};

// Custom hook for animated counter
const useAnimatedCounter = (end, duration = 2000, decimals = 0, suffix = "") => {
	const [count, setCount] = useState(0);
	const [isVisible, setIsVisible] = useState(false);
	const ref = useRef();

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isVisible) {
					setIsVisible(true);
				}
			},
			{threshold: 0.5},
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => observer.disconnect();
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible) return;

		let startTime;
		const startValue = 0;
		const endValue = typeof end === "string" ? parseFloat(end.replace(/[^0-9.]/g, "")) : end;

		const animate = (timestamp) => {
			if (!startTime) startTime = timestamp;
			const progress = Math.min((timestamp - startTime) / duration, 1);

			// Simplified easing for better performance
			const easeOut = 1 - (1 - progress) * (1 - progress);
			const currentValue = startValue + (endValue - startValue) * easeOut;

			if (decimals > 0) {
				setCount(currentValue.toFixed(decimals));
			} else {
				setCount(Math.floor(currentValue));
			}

			if (progress < 1) {
				requestAnimationFrame(animate);
			}
		};

		// Delay the animation start slightly to avoid too many simultaneous animations
		const timeout = setTimeout(() => {
			requestAnimationFrame(animate);
		}, 100);

		return () => clearTimeout(timeout);
	}, [isVisible, end, duration, decimals]);

	const formatCount = () => {
		if (typeof end === "string" && end.includes("K")) {
			return `${count}K${suffix}`;
		}
		return `${count}${suffix}`;
	};

	return {count: formatCount(), ref};
};

// Animated Counter Component
const AnimatedCounter = ({end, duration, decimals, suffix, className}) => {
	const {count, ref} = useAnimatedCounter(end, duration, decimals, suffix);

	return (
		<div ref={ref} className={className}>
			{count}
		</div>
	);
};

const HeroSection = () => {
	const badgeAnimation = useScrollAnimation(0.15, 0);
	const titleAnimation = useScrollAnimation(0.15, 100);
	const descriptionAnimation = useScrollAnimation(0.15, 150);
	const imageAnimation = useScrollAnimation(0.15, 200);

	const [width, setWidth] = useState(window.innerWidth);

	useEffect(() => {
		const handleResize = () => setWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<section className="hero-section py-5">
			<div className="container">
				<div className="row align-items-center g-5">
					<div className="col-lg-6">
						<div ref={badgeAnimation.ref} className={`badge bg-dark text-white rounded-pill px-3 py-2 mb-4 fade-in-element mobile-hero-badge-sm visible`}>
							<img src={`${process.env.PUBLIC_URL}/img/medal 1.png`} alt="Medal" style={{height: "20px", width: "auto"}} className="me-2" />
							We rate jobs, not people.
						</div>

						<h1 ref={titleAnimation.ref} className={`display-4 fw-bold mb-4 fade-in-element mobile-hero-title-sm visible d-none d-md-block`}>
							Every location has
							<br />
							<span className="text-success">Work History</span>
						</h1>

						<p ref={descriptionAnimation.ref} className={`lead text-muted mb-4 fade-in-element visible d-none d-md-block`}>
							JobSiteRating helps contractors make smarter decisions by revealing real, anonymous ratings of past experiences at service addresses.
						</p>

						<div className="stats-separator d-none d-md-block"></div>

						{width < 990 && <CheckJobSiteSection />}

						<div className=" d-none d-md-block">
							<div className="row stats-row mobile-hero-stats-row  d-lg-flex d-md-none">
								<div className="col-4 stats-column">
									<AnimatedCounter end={4.7} duration={2500} decimals={1} className="stat-number" />
									<div className="stat-label">Avg Rating</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/rating.png`} alt="Rating" />
									</div>
								</div>
								<div className="col-4 stats-column">
									<AnimatedCounter end="50K" duration={2000} suffix="+" className="stat-number" />
									<div className="stat-label">Reviews</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/review.png`} alt="Review" />
									</div>
								</div>
								<div className="col-4 stats-column">
									<AnimatedCounter end="25K" duration={2300} suffix="+" className="stat-number" />
									<div className="stat-label">Locations</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/location.png`} alt="Location" />
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="col-lg-6 mobile-hero-image-above">
						<div className=" d-none d-lg-block">
							<div ref={imageAnimation.ref} className={`text-center fade-in-element visible`}>
								<img src={`${process.env.PUBLIC_URL}/img/1 1.png`} alt="Construction Worker" className="img-fluid rounded" />
							</div>
							{/* Stats below image on mobile */}
							<div className="row stats-row mobile-hero-stats-below-wrapper d-lg-none">
								<div className="col-4 stats-column">
									<AnimatedCounter end={4.7} duration={2500} decimals={1} className="stat-number" />
									<div className="stat-label">Avg Rating</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/rating.png`} alt="Rating" />
									</div>
								</div>
								<div className="col-4 stats-column">
									<AnimatedCounter end="50K" duration={2000} suffix="+" className="stat-number" />
									<div className="stat-label">Reviews</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/review.png`} alt="Review" />
									</div>
								</div>
								<div className="col-4 stats-column">
									<AnimatedCounter end="25K" duration={2300} suffix="+" className="stat-number" />
									<div className="stat-label">Locations</div>
									<div className="stat-icon">
										<img src={`${process.env.PUBLIC_URL}/img/location.png`} alt="Location" />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default HeroSection;
